import { IconButton } from '@ads/components-react';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { CityViewModel } from '@/shared/api/generated';
import { DataTable } from '@/shared/ui/organisms/DataTable';

export interface CityTableProps {
  /** Página atual de cidades já fatiada para exibição. */
  readonly data: readonly CityViewModel[];
  readonly isLoading: boolean;
  /** Página atual (1-indexed). */
  readonly currentPage: number;
  /** Total de páginas (derivado client-side do total filtrado). */
  readonly totalPages: number;
  /** Total de registros filtrados (para o texto "Exibindo X–Y de Z"). */
  readonly totalCount: number;
  /** Itens por página. */
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  readonly emptyStateAction?: ReactNode;
  readonly onEdit: (city: CityViewModel) => void;
  readonly onDelete: (city: CityViewModel) => void;
}

/**
 * Tabela de Cidades. Renderiza Código, Nome, Estado, País, População e ações
 * (editar/excluir), delegando loading/empty/paginação ao `DataTable` e
 * habilitando ordenação client-side (na página atual). Fiel ao frame *Screen:
 * Cidades (Listagem)* de `design/location.pen`.
 *
 * TODO: a paginação é client-side (a lista chega completa da API). Quando o
 * backend paginar `GET /api/v1/location/cities`, usar `totalPages`/`totalCount`
 * do envelope da resposta em vez dos derivados na página. Ver `CITY_PAGE_SIZE`.
 */
export function CityTable({
  data,
  isLoading,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  emptyStateAction,
  onEdit,
  onDelete,
}: CityTableProps) {
  const { t } = useTranslation('location');

  const columns = useMemo<ColumnDef<CityViewModel, unknown>[]>(
    () => [
      {
        accessorKey: 'code',
        header: t('cities.table.columns.code'),
        cell: info => <span className="ts-paragraph-xxs">{(info.getValue() as string) ?? ''}</span>,
      },
      {
        accessorKey: 'name',
        header: t('cities.table.columns.name'),
        cell: info => (
          <span className="ts-paragraph-xxs !font-medium text-ctx-content-title">
            {(info.getValue() as string) ?? ''}
          </span>
        ),
      },
      {
        id: 'state',
        accessorKey: 'stateName',
        header: t('cities.table.columns.state'),
        cell: info => <span className="ts-paragraph-xxs">{(info.getValue() as string) ?? ''}</span>,
      },
      {
        id: 'country',
        accessorKey: 'countryName',
        header: t('cities.table.columns.country'),
        cell: info => <span className="ts-paragraph-xxs">{(info.getValue() as string) ?? ''}</span>,
      },
      {
        id: 'population',
        accessorKey: 'population',
        header: t('cities.table.columns.population'),
        cell: ({ row }) => (
          <span className="ts-paragraph-xxs">
            {row.original.population != null ? row.original.population.toLocaleString('pt-BR') : ''}
          </span>
        ),
      },
      {
        id: 'actions',
        header: () => <div className="text-right">{t('cities.table.columns.actions')}</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-quark">
            <IconButton
              icon={PencilSquareIcon}
              ariaLabel={t('cities.table.edit')}
              hierarchy="tertiary"
              size="md"
              onClick={() => onEdit(row.original)}
            />
            <IconButton
              icon={TrashIcon}
              ariaLabel={t('cities.table.delete')}
              hierarchy="tertiary"
              className="!text-base-danger-600"
              size="md"
              onClick={() => onDelete(row.original)}
            />
          </div>
        ),
      },
    ],
    [t, onEdit, onDelete],
  );

  return (
    <DataTable
      data={data as CityViewModel[]}
      columns={columns}
      isLoading={isLoading}
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={totalCount}
      pageSize={pageSize}
      onPageChange={onPageChange}
      emptyStateMessage={t('cities.table.empty')}
      emptyStateDescription={t('cities.table.emptyDescription')}
      {...(emptyStateAction ? { emptyStateAction } : {})}
      enableSorting
    />
  );
}
